<div class="modal">
  <header class="modal-header">
    <div>
      <p class="eyebrow">
        {{
          data.sheetId == null
            ? 'NEW FEEDBACK SHEET'
            : 'CHANGE FEEDBACK TARGETS'
        }}
      </p>
      <h2>
        {{
          data.sheetId == null
            ? '新しいフィードバックシートを作成'
            : '対象アセスメントを変更'
        }}
      </h2>
      <p>
        {{
          data.sheetId == null
            ? '対象アセスメントを確定すると、シートを作成してAI草案の非同期生成を開始します。'
            : '対象アセスメントを選び直してください。変更前に初期化される記入内容を確認できます。'
        }}
      </p>
    </div>
    <button
      class="icon-btn"
      type="button"
      aria-label="モーダルを閉じる"
      [disabled]="isCreating"
      (click)="close()"
    >
      <mat-icon aria-hidden="true">close</mat-icon>
    </button>
  </header>

  <div class="modal-body" #modalBody>
    @if (hasUnavailableCurrentTargets) {
      <p class="selection-notice" role="status">
        現在の対象のうち選択できなくなったアセスメントがあります。
      </p>
    }
    @if (isUnchangedSelectionBlocked) {
      <p id="unchanged-targets" class="selection-notice" role="status">
        現在の対象と同じです。別の候補を選ぶと変更できます。
      </p>
    }
    @if (isLoading) {
      <div class="loading-state" aria-live="polite">
        <mat-spinner diameter="30" aria-hidden="true" />
        <span>対象アセスメントを読み込み中です。</span>
      </div>
    } @else if (candidates.length === 0) {
      @if (!errorMessage) {
        <div class="empty-state" role="status">
          <mat-icon aria-hidden="true">event_busy</mat-icon>
          <strong>対象にできるアセスメントがありません。</strong>
          <span
            >このボードのスキルマップで完了したアセスメントがないか、すべて既存のシートで対象になっています。</span
          >
          <span
            >新しいアセスメントを実施するか、既存シートの対象を見直してください。</span
          >
        </div>
      }
    } @else {
      <div class="selection-summary" #selectionSummary>
        <div class="candidate-heading">
          <h3>対象アセスメントの候補</h3>
          <button
            type="button"
            class="candidate-help"
            [matTooltip]="candidateExplanation"
            matTooltipClass="candidate-explanation-tooltip"
            aria-label="候補の条件と並び順の説明"
          >
            <mat-icon aria-hidden="true">info</mat-icon>
          </button>
        </div>
        <p id="candidate-selection-limit" class="selection-limit">
          {{ selectionLimitMessage }}
        </p>
        <p class="selection-status" role="status" aria-live="polite">
          {{ selectionStatus }}
          @if (showSelectionLimit) {
            <span class="selection-limit-notice"
              >対象 1 または対象 2
              のチェックを外すと、別の候補を選べます。</span
            >
          }
        </p>
      </div>
      @if (data.sheetId == null && !hasAutomaticCandidates) {
        <p class="selection-notice">
          <mat-icon aria-hidden="true">info</mat-icon>
          <span
            >前回のシート作成以降に作成された対象アセスメントがないため、自動提案はありません。下の候補から対象アセスメントを選んでください。</span
          >
        </p>
      }
      <div class="candidate-table">
        <table aria-label="対象アセスメントの候補">
          <thead #candidateHead>
            <tr>
              <th scope="col">対象</th>
              <th scope="col" [attr.aria-sort]="ariaSort('assessDate')">
                <button
                  class="sort-button"
                  type="button"
                  (click)="onSort('assessDate')"
                >
                  実施日時
                  <mat-icon class="sort-indicator" aria-hidden="true">{{
                    sortIcon('assessDate')
                  }}</mat-icon>
                </button>
              </th>
              <th scope="col">対象アセスメント</th>
              <th scope="col" [attr.aria-sort]="ariaSort('overallScore')">
                <button
                  class="sort-button"
                  type="button"
                  (click)="onSort('overallScore')"
                >
                  総合
                  <mat-icon class="sort-indicator" aria-hidden="true">{{
                    sortIcon('overallScore')
                  }}</mat-icon>
                </button>
              </th>
              <th
                scope="col"
                [attr.aria-sort]="ariaSort('focusKeyActionAverageScore')"
              >
                <button
                  class="sort-button"
                  type="button"
                  (click)="onSort('focusKeyActionAverageScore')"
                >
                  重点KA平均
                  <mat-icon class="sort-indicator" aria-hidden="true">{{
                    sortIcon('focusKeyActionAverageScore')
                  }}</mat-icon>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (
              row of displayedCandidateRows;
              track row.candidate.assessmentId
            ) {
              @let candidate = row.candidate;
              @let isSelectionBlocked =
                row.targetNumber === 0 && isSelectionLimitReached;
              <tr class="candidate-row" [class.selected]="row.targetNumber > 0">
                <td>
                  <label class="candidate-checkbox-label">
                    <input
                      class="candidate-checkbox"
                      type="checkbox"
                      [checked]="row.targetNumber > 0"
                      [disabled]="isCreating"
                      [attr.aria-disabled]="isSelectionBlocked ? true : null"
                      [attr.aria-describedby]="
                        isSelectionBlocked ? 'candidate-selection-limit' : null
                      "
                      [attr.aria-label]="row.checkboxLabel"
                      (click)="toggleCandidate(candidate.assessmentId, $event)"
                    />
                  </label>
                </td>
                <td>
                  {{ row.assessDateText }}
                  @if (!candidate.isAfterLatestSheet) {
                    <span
                      class="candidate-period"
                      aria-label="前回のシート作成より前に取り込まれたアセスメント（実施日とは異なります）"
                      title="前回のシート作成より前に取り込まれたアセスメントです（実施日とは異なります）"
                      >前回シート作成前に取り込み</span
                    >
                  }
                </td>
                <td>
                  <strong>{{ row.meetingTitleText }}</strong>
                  @if (row.targetNumber > 0) {
                    <span class="candidate-status"
                      >対象 {{ row.targetNumber }}</span
                    >
                  }
                  @if (row.isAutomatic) {
                    <span class="automatic-badge">自動提案</span>
                  }
                  @if (row.selectionReason) {
                    <span class="candidate-reason">{{
                      row.selectionReason
                    }}</span>
                  }
                  <a
                    class="assessment-link"
                    [routerLink]="['/home']"
                    [queryParams]="row.linkParams"
                    target="_blank"
                    rel="noopener noreferrer"
                    [attr.aria-label]="row.linkLabel"
                  >
                    <mat-icon aria-hidden="true">open_in_new</mat-icon>
                    アセスメントを開く
                  </a>
                  @if (candidate.tags.length > 0) {
                    <span class="assessment-tags" aria-label="アセスメントタグ">
                      @for (tag of candidate.tags; track tag) {
                        <span class="assessment-tag">{{ tag }}</span>
                      }
                    </span>
                  }
                </td>
                <td>{{ row.overallScoreText }}</td>
                <td>
                  @if (row.focusScoreText != null) {
                    <strong>{{ row.focusScoreText }}</strong>
                  } @else {
                    -
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    @if (errorMessage) {
      <p class="operation-error" role="alert">{{ errorMessage }}</p>
    }
  </div>

  <footer class="modal-footer">
    <p>
      {{
        data.sheetId == null
          ? '作成後も全記入欄は編集可能です。草案は空欄にのみ反映されます。'
          : '変更しない対象の記入内容は残り、外した対象の記入内容だけが削除されます。すべて入れ替える場合は主任側の課題仮説・特記事項も初期化します。AI 草案は空欄を埋める形で作り直します。'
      }}
    </p>
    <div class="modal-actions">
      <button
        class="btn btn-secondary"
        type="button"
        [disabled]="isCreating"
        (click)="close()"
      >
        キャンセル
      </button>
      <button
        class="btn btn-primary create-sheet"
        type="button"
        [disabled]="!canCreate"
        [attr.aria-describedby]="
          isUnchangedSelectionBlocked ? 'unchanged-targets' : null
        "
        (click)="create()"
      >
        <mat-icon aria-hidden="true">
          {{
            data.sheetId == null
              ? 'add'
              : canRegenerateAiDraft
                ? 'refresh'
                : 'swap_horiz'
          }}
        </mat-icon>
        {{ createButtonLabel }}
      </button>
    </div>
  </footer>
</div>
